import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { App, URLOpenListenerEvent } from "@capacitor/app";
import { isNativeApp } from "@/lib/isNativeApp";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook to handle deep links in native Capacitor apps.
 * Captures URLs like easyfinder://auth/callback?code=... and navigates accordingly.
 */
export const useDeepLinks = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Only set up deep link handling on native platforms
    if (!isNativeApp()) {
      return;
    }

    window.__DEBUG_LOG__?.('🔗 DeepLink handler initialized');

    // Listen for app state changes (background/foreground)
    const stateListener = App.addListener('appStateChange', (state) => {
      window.__DEBUG_LOG__?.(`📱 App state: ${state.isActive ? 'ACTIVE (foreground)' : 'BACKGROUND'}`);
    });

    const handleUrlOpen = async (event: URLOpenListenerEvent) => {
      const url = event.url;
      window.__DEBUG_LOG__?.(`🔗 DeepLink received: ${url}`);
      console.log("[DeepLink] Received URL:", url);

      try {
        // Parse the deep link URL
        // Format: easyfinder://auth/callback?code=... OR easyfinder://oauth/auth?access_token=...
        const urlObj = new URL(url);
        const path = urlObj.pathname || urlObj.host; // pathname for standard URLs, host for custom schemes
        const search = urlObj.search;
        const params = new URLSearchParams(search);

        window.__DEBUG_LOG__?.(`📍 Path: ${path}, Host: ${urlObj.host}`);
        console.log("[DeepLink] Parsed:", { path, search, host: urlObj.host });

        // Handle auth callback - detect BOTH patterns:
        // - auth/callback (standard)
        // - oauth/auth (Apple edge function redirect)
        const isAuthCallback = url.includes("auth/callback") || url.includes("oauth/auth");
        
        if (isAuthCallback) {
          window.__DEBUG_LOG__?.('🔐 Auth callback detected');
          
          const code = params.get("code");
          const accessToken = params.get("access_token");
          const refreshToken = params.get("refresh_token");
          const error = params.get("error");
          const errorDescription = params.get("error_description");

          window.__DEBUG_LOG__?.(`🔑 Tokens: access=${!!accessToken}, refresh=${!!refreshToken}, code=${!!code}, error=${!!error}`);
          console.log("[DeepLink] Auth callback:", { 
            hasCode: !!code, 
            hasAccessToken: !!accessToken, 
            hasError: !!error 
          });

          // Handle error
          if (error) {
            window.__DEBUG_LOG__?.(`❌ OAuth error: ${error} - ${errorDescription}`);
            console.error("[DeepLink] OAuth error:", error, errorDescription);
            navigate("/auth", { replace: true });
            return;
          }

          // If we have access_token, set session directly
          if (accessToken) {
            window.__DEBUG_LOG__?.('⏳ Setting session from tokens...');
            console.log("[DeepLink] Setting session from access token");

            try {
              const { data, error: setSessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken || "",
              });

              console.log("[DeepLink] setSession result:", {
                hasData: !!data,
                hasSession: !!data?.session,
                hasUser: !!data?.session?.user,
                userId: data?.session?.user?.id,
                error: setSessionError?.message,
              });

              if (setSessionError) {
                window.__DEBUG_LOG__?.(`❌ setSession error: ${setSessionError.message}`);
                console.error("[DeepLink] setSession error:", setSessionError);
                navigate(`/auth?error=${encodeURIComponent(setSessionError.message)}`, {
                  replace: true,
                });
                return;
              }

              if (!data?.session) {
                window.__DEBUG_LOG__?.('❌ No session returned from setSession');
                console.error("[DeepLink] No session returned from setSession");
                navigate("/auth?error=no_session", { replace: true });
                return;
              }

              window.__DEBUG_LOG__?.(`✅ Session set! User: ${data.session.user.id.substring(0, 8)}...`);

              // Wait for AuthContext hydration (prevents race condition on iOS WebView)
              window.__DEBUG_LOG__?.('⏳ Waiting for AuthContext hydration...');
              console.log("[DeepLink] setSession successful, waiting for AuthContext...");
              await new Promise((resolve) => setTimeout(resolve, 1500));

              window.__DEBUG_LOG__?.('🚀 Navigating to /documents');
              console.log("[DeepLink] Now safe to navigate to documents");
              navigate("/documents", { replace: true });
              return;
            } catch (err: any) {
              // CRITICAL: Log the raw error object first (iOS WebView sometimes shows `{}`)
              window.__DEBUG_LOG__?.(`❌ Exception: ${err?.message || String(err)}`);
              console.error("[DeepLink] RAW EXCEPTION:", err);
              console.error("[DeepLink] Exception type:", typeof err);
              console.error("[DeepLink] Exception constructor:", err?.constructor?.name);

              let rawJson = "<unserializable>";
              try {
                rawJson = JSON.stringify(err, null, 2);
              } catch {
                // ignore
              }

              const errorDetails = {
                message: err?.message || String(err),
                name: err?.name || "UnknownError",
                stack: err?.stack || "No stack trace",
                raw: rawJson,
              };

              console.error("[DeepLink] Error details:", errorDetails);
              window.__DEBUG_LOG__?.(`❌ Full error: ${JSON.stringify(errorDetails)}`);

              // Intentionally do not navigate yet — we want the full error in native logs first.
              return;
            }
          }

          // If we have code, exchange it for session
          if (code) {
            window.__DEBUG_LOG__?.('🔄 Exchanging code for session...');
            console.log("[DeepLink] Exchanging code for session");
            const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

            if (exchangeError) {
              window.__DEBUG_LOG__?.(`❌ Code exchange error: ${exchangeError.message}`);
              console.error("[DeepLink] Code exchange error:", exchangeError);
              navigate("/auth", { replace: true });
              return;
            }

            window.__DEBUG_LOG__?.('✅ Code exchanged successfully');

            // Session set successfully, check onboarding and redirect
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              const { data: profile } = await supabase
                .from("profiles")
                .select("onboarding_completed")
                .eq("id", session.user.id)
                .single();

              if (profile?.onboarding_completed) {
                window.__DEBUG_LOG__?.('🚀 Navigating to /documents');
                navigate("/documents", { replace: true });
              } else {
                window.__DEBUG_LOG__?.('🚀 Navigating to /onboarding');
                navigate("/onboarding", { replace: true });
              }
              return;
            }
          }

          // Fallback: no valid auth data
          window.__DEBUG_LOG__?.('⚠️ No valid auth data found, redirecting to /auth');
          console.log("[DeepLink] No valid auth data, redirecting to /auth");
          navigate("/auth", { replace: true });
          return;
        }

        // Handle simple auth redirect (from error cases)
        if (url === "easyfinder://auth" || url.includes("easyfinder://auth?")) {
          window.__DEBUG_LOG__?.('↩️ Auth redirect, navigating to /auth');
          console.log("[DeepLink] Auth redirect, navigating to /auth");
          navigate("/auth", { replace: true });
          return;
        }

        // Handle other deep links (add more as needed)
        if (path) {
          window.__DEBUG_LOG__?.(`📍 Navigating to: /${path}${search}`);
          navigate(`/${path}${search}`, { replace: true });
        }
      } catch (error: any) {
        window.__DEBUG_LOG__?.(`❌ Parse error: ${error?.message || String(error)}`);
        console.error("[DeepLink] Error parsing URL:", error);
      }
    };

    // Listen for app URL open events
    const listener = App.addListener("appUrlOpen", handleUrlOpen);

    // Cleanup listeners on unmount
    return () => {
      listener.then((l) => l.remove());
      stateListener.then((l) => l.remove());
    };
  }, [navigate]);
};
